/**
 * AssistantPanelFrame — shared right-side slide-in panel frame used by
 * the channel chooser and the (future PR 7) chat placeholder. Mirrors
 * the outer container of `VoiceBotPanel` so positioning, animation,
 * backdrop, and dismissal semantics stay 1:1 across all modes.
 *
 * NOTE — VoiceBotPanel intentionally keeps its own frame inline because
 * its frame is intertwined with internal state (CalendlyOverlay opacity,
 * isMobile, isUploading, isSessionActive). Extracting it would risk the
 * voice flow. This component mirrors the visible result for the simpler
 * chooser/chat-placeholder paths only.
 */

import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Backdrop } from './Backdrop';

interface AssistantPanelFrameProps {
  isOpen: boolean;
  onClose: () => void;
  panelWidth: number;
  /** Render direction — 'rtl' for he/ar, 'ltr' for en/ru/fr. */
  dir: 'rtl' | 'ltr';
  /**
   * If true the frame swallows Escape key inside the panel and forwards
   * to onClose. Used by the chooser; disabled when the inner content
   * needs to handle its own keyboard.
   */
  closeOnEscape?: boolean;
  children: React.ReactNode;
}

export const AssistantPanelFrame: React.FC<AssistantPanelFrameProps> = ({
  isOpen,
  onClose,
  panelWidth,
  dir,
  closeOnEscape = true,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  // Save the element that opened us so we can restore focus on close.
  useEffect(() => {
    if (isOpen) {
      lastFocusedRef.current = (document.activeElement as HTMLElement | null) ?? null;
    } else if (lastFocusedRef.current) {
      try {
        lastFocusedRef.current.focus();
      } catch {
        // ignore
      }
      lastFocusedRef.current = null;
    }
  }, [isOpen]);

  // Focus first focusable element inside the panel when opened.
  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => {
      const node = containerRef.current;
      if (!node) return;
      const focusable = node.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) focusable.focus();
      else node.focus();
    }, 50);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  // Escape closes when allowed.
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeOnEscape, onClose]);

  // Tab trap — keep focus inside the panel.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const node = containerRef.current;
      if (!node) return;
      const focusables = Array.from(
        node.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Backdrop key="backdrop" onClick={onClose} />
          <motion.div
            key="panel"
            ref={containerRef}
            tabIndex={-1}
            initial={{ opacity: 0, x: panelWidth }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: panelWidth }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              height: '100dvh',
              width: panelWidth,
              maxWidth: '100vw',
              backgroundColor: 'white',
              boxShadow: '-4px 0 30px rgba(0, 0, 0, 0.15)',
              zIndex: 10000,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              fontFamily:
                'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              direction: dir,
              outline: 'none',
            }}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AssistantPanelFrame;
